import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Leaf, Users, Building, Banknote } from 'lucide-react';

export default function DevelopmentPage() {
  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">Sugar's Role in Development</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Sugar industries play a vital role in economic development, job creation, and rural 
            livelihoods across ACP and LDC countries.
          </p>
        </div>

        {/* Economic Impact */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Economic Impact</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <Banknote className="w-12 h-12 text-amber-600 mx-auto mb-4" />
                <CardTitle>Export Revenue</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Sugar exports generate billions in foreign exchange earnings for member countries, 
                  supporting balance of payments and economic stability.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <Users className="w-12 h-12 text-amber-600 mx-auto mb-4" />
                <CardTitle>Employment</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Direct and indirect employment for millions of people, from farmers to factory 
                  workers, transporters, and support services.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <Building className="w-12 h-12 text-amber-600 mx-auto mb-4" />
                <CardTitle>Infrastructure</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Development of rural infrastructure including roads, railways, ports, and 
                  processing facilities that benefit entire regions.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <Leaf className="w-12 h-12 text-amber-600 mx-auto mb-4" />
                <CardTitle>Rural Development</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Anchor for rural economic activity, preventing urban migration and supporting 
                  sustainable agricultural communities.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Development Goals */}
        <section className="mb-16">
          <div className="grid lg:grid-cols-2 align-items-center gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Contributing to SDGs</h2>
              <p className="text-lg text-gray-600 mb-6">
                Sugar industries in ACP and LDC countries contribute significantly to multiple 
                Sustainable Development Goals, creating a foundation for broader economic and 
                social progress.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-amber-600 rounded-full mt-2"></div>
                  <div>
                    <strong className="text-gray-900">No Poverty:</strong>
                    <span className="text-gray-600"> Providing stable income for rural families</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-amber-600 rounded-full mt-2"></div>
                  <div>
                    <strong className="text-gray-900">Decent Work:</strong>
                    <span className="text-gray-600"> Creating formal employment opportunities</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-amber-600 rounded-full mt-2"></div>
                  <div>
                    <strong className="text-gray-900">Economic Growth:</strong>
                    <span className="text-gray-600"> Contributing to GDP and export earnings</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-amber-600 rounded-full mt-2"></div>
                  <div>
                    <strong className="text-gray-900">Responsible Production:</strong>
                    <span className="text-gray-600"> Implementing sustainable farming practices</span>
                  </div>
                </div>
              </div>
            </div>
            <div className='h-full'>
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1500215238115-1322078d92a3?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                alt="Sustainable farming practices"
                className="rounded-lg shadow-lg w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Value Chain Development */}
        <section className="mb-16 bg-gray-50 rounded-lg p-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Value Chain Development</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-amber-100 p-4 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <Leaf className="w-10 h-10 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Primary Production</h3>
              <p className="text-gray-600">
                Supporting smallholder farmers with improved varieties, technical assistance, 
                and fair pricing mechanisms to enhance productivity and incomes.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-amber-100 p-4 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <Building className="w-10 h-10 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Processing & Manufacturing</h3>
              <p className="text-gray-600">
                Modernizing sugar mills and processing facilities to improve efficiency, 
                quality, and create higher-value products for domestic and export markets.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-amber-100 p-4 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <Users className="w-10 h-10 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Market Access</h3>
              <p className="text-gray-600">
                Facilitating access to domestic, regional, and international markets through 
                trade advocacy, quality standards, and market intelligence.
              </p>
            </div>
          </div>
        </section>

        {/* Case Studies */}
        {/* <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Development Success Stories</h2>
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle>Mauritius: Economic Diversification</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base mb-4">
                  Mauritius successfully leveraged its sugar industry as a foundation for broader 
                  economic development, transitioning from a mono-crop economy to a diversified, 
                  upper-middle-income country.
                </CardDescription>
                <p className="text-sm text-gray-500">
                  Key factors: Strategic investment in education, infrastructure development, 
                  and gradual economic diversification while maintaining sugar as an anchor industry.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle>Eswatini: Smallholder Integration</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base mb-4">
                  Successful integration of smallholder farmers into commercial sugar production 
                  through outgrower schemes, providing stable incomes and rural development.
                </CardDescription>
                <p className="text-sm text-gray-500">
                  Impact: Thousands of small farmers benefiting from guaranteed markets, 
                  technical support, and improved farming practices.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle>Zambia: Rural Transformation</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base mb-4">
                  Sugar estates in Zambia have driven rural transformation through infrastructure 
                  development, health services, and educational facilities for surrounding communities.
                </CardDescription>
                <p className="text-sm text-gray-500">
                  Benefits: Schools, clinics, roads, and other social infrastructure serving 
                  wider communities beyond direct employees.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle>Mozambique: Post-Conflict Recovery</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base mb-4">
                  Sugar industry revival has played a crucial role in post-conflict economic 
                  recovery, providing employment and foreign exchange earnings.
                </CardDescription>
                <p className="text-sm text-gray-500">
                  Achievement: Restoration of sugar production capacity and creation of 
                  thousands of jobs in previously war-affected areas.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Challenges and Opportunities */}
        {/* <section className="bg-amber-50 rounded-lg p-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Challenges and Opportunities</h2>
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Current Challenges</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Climate change impacts on production</li>
                <li>• Global market price volatility</li>
                <li>• Competition from other sweeteners</li>
                <li>• Need for technology modernization</li>
                <li>• Sustainability and environmental concerns</li>
                <li>• Access to finance for smallholder farmers</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Future Opportunities</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Regional market integration and expansion</li>
                <li>• Value-added products development</li>
                <li>• Renewable energy from bagasse</li>
                <li>• Sustainable and organic sugar markets</li>
                <li>• Digital agriculture technologies</li>
                <li>• Carbon credit opportunities</li>
              </ul>
            </div>
          </div>
        </section> */}
      </div>
    </div>
  );
}